import React, { useEffect, useRef, useState } from "react";

import "./CopyToClipboardButton.scss";

interface Props {
  text: string;
}

export const CopyToClipboardButton = ({ text }: Props) => {
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");
  const resetTimer = useRef<number>();
  const mounted = useRef(false);
  const copying = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.clearTimeout(resetTimer.current);
    };
  }, []);

  const copyToClipboard = async () => {
    if (copying.current) return;
    copying.current = true;

    try {
      await navigator.clipboard.writeText(text);
      if (!mounted.current) return;

      window.clearTimeout(resetTimer.current);
      setStatus("copied");
      resetTimer.current = window.setTimeout(() => setStatus("idle"), 2000);
    } catch {
      if (!mounted.current) return;

      window.clearTimeout(resetTimer.current);
      setStatus("error");
    } finally {
      copying.current = false;
    }
  };

  return (
    <>
      <button
        type="button"
        className="CopyToClipboardButton"
        data-status={status}
        aria-label={
          status === "idle"
            ? "Copy to clipboard"
            : status === "copied"
              ? "Copied!"
              : "Copy failed. Try again"
        }
        onClick={copyToClipboard}
      >
        <span className="CopyToClipboardButton__label" aria-hidden="true">
          Copy to clipboard
        </span>
        <span className="CopyToClipboardButton__feedback" aria-hidden="true">
          {status === "error" ? "Try again" : "Copied!"}
        </span>
      </button>
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {status === "copied"
          ? "Copied!"
          : status === "error"
            ? "Couldn't copy to clipboard. Please try again."
            : ""}
      </span>
    </>
  );
};
